import { useState, useEffect, useContext } from "react";
import API from '../../utils/API';
import Swal from 'sweetalert2';
import LoaderWheel from "../LoaderWheel";
import GlobalSVG from '../../utils/GlobalSVG';
import { AppContext } from "../../utils/ContextProvider";

function UtilityBillLine(props) {
    const [edit, setEdit] = useState(false)
    const context = useContext(AppContext);
    const [deleting, setDeleting] = useState(false)
    const [downloading, setDownloading] = useState(false)
    const [bill, setBill] = useState({
        "utility_bill_account_id": props.utility_bill_account_id,
        "bill_id": "",
        "bill_month": "",
        "bill_year": "",
        "basic_monthly": "",
        "kwh_consumption": "",
        "kwh_cost": "",
        "kva_consumption": "",
        "kva_cost": "",
        "water_consumption": "",
        "water_cost": "",
        "sewage_consumption": "",
        "sewage_cost": "",
        "gas_consumption": "",
        "gas_cost": "",
        "delivery_charges": "",
        "other_charges": "",
        "carbon_charge": "",
        "municipal_tax": "",
        "gst_cost": "",
        "pst_cost": "",
        "emissions": ""
    })

    const totalList = [
        "basic_monthly",
        "kwh_cost",
        "kva_cost",
        "water_cost",
        "sewage_cost",
        "gas_cost",
        "delivery_charges",
        "other_charges",
        "carbon_charge",
        "municipal_tax",
        "gst_cost",
        "pst_cost",
    ]

    const [acceptable, setAcceptable] = useState(false)
    // const [error, setError] = useState(null)
    const [loading, setLoading] = useState(null)
    const monthsDict = {
        1: "Jan",
        2: "Feb",
        3: "Mar",
        4: "Apr",
        5: "May",
        6: "Jun",
        7: "Jul",
        8: "Aug",
        9: "Sep",
        10: "Oct",
        11: "Nov",
        12: "Dec"
    };

    function convertFromM3(value, unit) {
        if (value == null || isNaN(value)) return null;
        switch (unit) {
            case "ft3":
                return value * 35.3147;
            case "l":
                return value * 1000;
            case "GJ":
                return value * 0.038;
            case "m3":
                return value;
            default:
                throw new Error(`Unsupported unit: ${unit}`);
        }
    }

    function convertFromL(value, unit) {
        if (value == null || isNaN(value)) return null;
        switch (unit) {
            case "ft3":
                return value * 0.0353147;
            case "l":
                return value;
            case "m3":
                return value / 1000;
            default:
                throw new Error(`Unsupported unit: ${unit}`);
        }
    }

    function convertToM3(value, unit) {
        if (value == null || isNaN(value)) return null;
        switch (unit) {
            case "ft3":
                return value / 35.3147;
            case "l":
                return value / 1000;
            case "GJ":
                return value / 0.038;
            case "m3":
                return value;
            default:
                throw new Error(`Unsupported unit: ${unit}`);
        }
    }

    function convertToL(value, unit) {
        if (value == null || isNaN(value)) return null;
        switch (unit) {
            case "ft3":
                return value / 0.0353147;
            case "l":
                return value;
            case "m3":
                return value * 1000;
            default:
                throw new Error(`Unsupported unit: ${unit}`);
        }
    }

    useEffect(() => {
        if (!props.value) return
        const gasCons = props.value.gas_consumption
        const waterCons = props.value.water_consumption
        const sewerCons = props.value.sewer_consumption
        setBill({
            ...props.value,
            gas_consumption: gasCons ? convertFromM3(gasCons, props.gasUnit) : null,
            water_consumption: waterCons ? convertFromL(waterCons, props.waterUnit) : null,
            sewer_consumption: sewerCons ? convertFromL(sewerCons, props.sewerUnit) : null
        })
    }, [props.value, props.gasUnit, props.waterUnit, props.sewerUnit])

    const updateValue = (e) => {
        const { name, value } = e.target;
        setBill(prevValue => {
            return {
                ...prevValue,
                [name]: value.trim()
            }
        })
    }

    useEffect(() => {
        let mandatory = [
            "bill_month",
            "bill_year",
            "basic_monthly",
            "kwh_consumption",
            "kwh_cost",
            "kva_consumption",
            "kva_cost",
            "water_consumption",
            "water_cost",
            "sewage_consumption",
            "sewage_cost",
            "gas_consumption",
            "gas_cost",
            "delivery_charges",
            "other_charges",
            "carbon_charge",
            "municipal_tax",
            "gst_cost",
            "pst_cost",
            "emissions"
        ]

        for (let item of mandatory) {
            if (bill[item] !== '') continue
            setAcceptable(false)
            return
        }
        setAcceptable(true)

    }, [bill])

    const postValue = async (e) => {

        let tempValue = { ...bill }
        const toFormat = [
            "basic_monthly",
            "kwh_consumption",
            "kwh_cost",
            "kva_consumption",
            "kva_cost",
            "water_consumption",
            "water_cost",
            "sewage_consumption",
            "sewage_cost",
            "gas_consumption",
            "gas_cost",
            "delivery_charges",
            "other_charges",
            "carbon_charge",
            "municipal_tax",
            "gst_cost",
            "pst_cost",
            "emissions",
        ]
        tempValue = {
            ...tempValue,
            gas_consumption: tempValue.gas_consumption ? convertToM3(tempValue.gas_consumption, props.gasUnit) : null,
            water_consumption: tempValue.water_consumption ? convertToL(tempValue.water_consumption, props.waterUnit) : null,
            sewer_consumption: tempValue.sewer_consumption ? convertToL(tempValue.sewer_consumption, props.sewerUnit) : null,
        }

        for (let item of toFormat) {
            tempValue[item] = tempValue[item] ? parseFloat(tempValue[item]).toFixed(2) : tempValue[item]
        }
        tempValue = Object.fromEntries(Object.entries(tempValue).filter(([k]) => [...toFormat, "bill_month", "bill_year"].includes(k)));
        setLoading(true)

        try {
            await API.putUtilityBill(tempValue, bill.id)
            setEdit(false)
            props.setUtilities(prev => {
                let tempArr = JSON.parse(JSON.stringify(prev))
                const accountIndex = tempArr.findIndex(account => account.id === bill.utility_bill_account_id)
                if (accountIndex === -1) return
                const billIndex = tempArr[accountIndex].bills.findIndex(b => b.id === bill.id)
                if (billIndex === -1) return prev;
                tempArr[accountIndex].bills.splice(billIndex, 1, bill)
                return tempArr
            })
        } catch (err) {
            console.log(err)
            if (err.message === 'cancelling') return
            if (err.response && err.response.data) {
                try {
                    if ('general' in err.response.data || 'message' in err.response.data) {
                        Swal.fire({
                            title: 'Error.',
                            text: err.response.data.general,
                            icon: 'warning',
                            confirmButtonText: 'Ok',
                            confirmButtonColor: '#007b4e',
                        });
                    }
                } catch (e) {
                    Swal.fire({
                        title: 'Error.',
                        text: err.response.data,
                        icon: 'warning',
                        confirmButtonText: 'Ok',
                        confirmButtonColor: '#007b4e',
                    });
                }
            } else {
                Swal.fire({
                    title: 'Error.',
                    text: 'An error occurred, please try again later.',
                    icon: 'warning',
                    confirmButtonText: 'Ok',
                    confirmButtonColor: '#007b4e',
                });
            }
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        setDeleting(true)
        try {
            Swal.fire({
                title: 'Are you sure?',
                text: 'You will not be able to revert this!',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, delete it!',
                cancelButtonText: 'No, cancel!',
                reverseButtons: true
            }).then(async (result) => {
                if (result.isConfirmed) {
                    await API.putUtilityBill({ deleted: 1 }, bill.id)
                    props.setUtilities(prev => {
                        let tempArr = JSON.parse(JSON.stringify(prev))
                        const accountIndex = tempArr.findIndex(account => account.id === bill.utility_bill_account_id)
                        if (accountIndex === -1) return
                        const billIndex = tempArr[accountIndex].bills.findIndex(b => b.id === bill.id)
                        if (billIndex === -1) return prev;
                        tempArr[accountIndex].bills.splice(billIndex, 1)
                        return tempArr
                    })
                } else {
                    setDeleting(false)
                }
            })
        } catch (err) {
            console.log(err)
            if (err.message === 'cancelling') return
            if (err.response && err.response.data) {
                try {
                    if ('general' in err.response.data || 'message' in err.response.data) {
                        Swal.fire({
                            title: 'Error.',
                            text: err.response.data.general,
                            icon: 'warning',
                            confirmButtonText: 'Ok',
                            confirmButtonColor: '#007b4e',
                        });
                    }
                } catch (e) {
                    Swal.fire({
                        title: 'Error.',
                        text: err.response.data,
                        icon: 'warning',
                        confirmButtonText: 'Ok',
                        confirmButtonColor: '#007b4e',
                    });
                }
            } else {
                Swal.fire({
                    title: 'Error.',
                    text: 'An error occurred, please try again later.',
                    icon: 'warning',
                    confirmButtonText: 'Ok',
                    confirmButtonColor: '#007b4e',
                });
            }
        } finally {
            setDeleting(false)
        }

    }

    const handleDownload = async (id) => {
        try {
            setDownloading(true)
            const cancelToken = API.cancelToken();
            const newWindow = window.open('', '_blank');
            const { data } = await API.getBillLink(cancelToken, id);

            if (!data?.url) {
                newWindow.close();
                throw new Error("No download URL returned");
            }

            newWindow.location.href = data.url;

        } catch (err) {
            console.error(err);
            Swal.fire({
                title: 'Download failed',
                text: `An error occurred: ${err?.response?.data ?? "Unknown Error"}`,
                icon: 'warning',
                confirmButtonText: 'Ok',
                confirmButtonColor: '#007b4e',
            });
        } finally {
            setDownloading(false)
        }
    };

    return (
        <div className='w-full overflow-x-auto'>
            {bill && <div className='min-w-[1300px] flex flex-nowrap items-center input-border p-4 gap-6'>
                <div className='tablet:w-1/12 mobile:w-full'>
                    <div className='tablet:w-1/12 mobile:w-full min-w-[80px] mb-4'>
                        <p className='text-sm mb-1'>Month</p>
                        <select
                            name="bill_month"
                            className='text-sm input-color outline-none w-full hide-input-background'
                            disabled={!edit}
                            value={bill.bill_month ? bill.bill_month : ""}
                            onChange={updateValue}>
                            <option value="">Month</option>
                            {
                                Object.keys(monthsDict).map((e, ix) => (
                                    <option key={ix + "billinngmonth"} value={e}>{monthsDict[e]}</option>
                                ))
                            }
                        </select>
                    </div>
                    <div className='tablet:w-1/12 mobile:w-full min-w-[80px] mb-4'>
                        <p className='text-sm mb-1'>Year</p>
                        <select
                            name="bill_year"
                            className='text-sm input-color outline-none w-full hide-input-background'
                            disabled={!edit}
                            value={bill.bill_year !== "" ? bill.bill_year : ""}
                            onChange={updateValue}
                        >
                            <option value="">Year</option>
                            {
                                Array.from({ length: new Date().getFullYear() - 1999 }, (_, i) => {
                                    const year = 2000 + i;
                                    return (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    );
                                })
                            }
                        </select>
                    </div>
                </div>
                <div className='w-[110px]'>
                    <div className='mb-4'>
                        <p className='text-sm mb-1'>Bill ID</p>
                        <div className='flex flex-nowrap items-center'>
                            <input
                                placeholder='Bill ID'
                                className='text-sm input-color outline-none w-full hide-input-background p-1'
                                type="text"
                                disabled
                                name="bill_id"
                                defaultValue={bill.bill_id !== "" ? bill.bill_id : ""}
                                onChange={updateValue} />
                        </div>
                    </div>
                    <div className='mb-4'>
                        <p className='text-sm mb-1'>Monthly</p>
                        <div className='flex flex-nowrap items-center'>
                            <span className='mr-1 text-sm'>$</span>
                            <input
                                placeholder='Price'
                                className='hide-input-background text-sm input-color outline-none w-full p-1'
                                type="number"
                                step="0.1"
                                disabled={!edit}
                                name="basic_monthly"
                                defaultValue={bill.basic_monthly !== "" ? bill.basic_monthly : ""}
                                onChange={updateValue} />
                        </div>
                    </div>
                </div>
                {
                    props.type === 0 &&
                    <div className='w-[120px]'>
                        <div className='mb-4'>
                            <p className='text-sm mb-1'>kWh Consumption</p>
                            <div className='flex flex-nowrap items-center'>
                                <input
                                    placeholder='kWh Consumption'
                                    className='hide-input-background text-sm input-color outline-none w-full p-1'
                                    type="number"
                                    step="0.1"
                                    disabled={!edit}
                                    name="kwh_consumption"
                                    defaultValue={bill.kwh_consumption !== "" ? bill.kwh_consumption : ""}
                                    onChange={updateValue} />
                                <span className='ml-1 text-sm'>kWh</span>
                            </div>
                        </div>
                        <div className='mb-4'>
                            <p className='text-sm mb-1'>kWh Cost</p>
                            <div className='flex flex-nowrap items-center'>
                                <span className='mr-1 text-sm'>$</span>
                                <input
                                    placeholder='kwh_cost'
                                    className='hide-input-background text-sm input-color outline-none w-full p-1'
                                    type="number"
                                    step="0.1"
                                    disabled={!edit}
                                    name="kwh_cost"
                                    defaultValue={bill.kwh_cost !== "" ? bill.kwh_cost : ""}
                                    onChange={updateValue} />
                            </div>
                        </div>
                    </div>
                }
                {
                    props.type === 0 &&
                    <div className='w-[110px]'>
                        <div className='mb-4'>
                            <p className='text-sm mb-1'>kVA Peak</p>
                            <div className='flex flex-nowrap items-center'>
                                <input
                                    placeholder='kVA Peak'
                                    className='hide-input-background text-sm input-color outline-none w-full p-1'
                                    type="number"
                                    step="0.1"
                                    disabled={!edit}
                                    name="kva_consumption"
                                    defaultValue={bill.kva_consumption !== "" ? bill.kva_consumption : ""}
                                    onChange={updateValue} />
                                <span className='ml-1 text-sm'>kVA</span>
                            </div>
                        </div>
                        <div className='mb-4'>
                            <p className='text-sm mb-1'>kVA Cost</p>
                            <div className='flex flex-nowrap items-center'>
                                <span className='mr-1 text-sm'>$</span>
                                <input
                                    placeholder='kVA Cost'
                                    className='hide-input-background text-sm input-color outline-none w-full p-1'
                                    type="number"
                                    step="0.1"
                                    disabled={!edit}
                                    name="kva_cost"
                                    defaultValue={bill.kva_cost !== "" ? bill.kva_cost : ""}
                                    onChange={updateValue} />
                            </div>
                        </div>
                    </div>
                }
                {
                    props.type === 1 &&
                    <div className='w-[130px]'>
                        <div className='mb-4'>
                            <p className='text-sm mb-1'>Gas Consumption</p>
                            <div className='flex flex-nowrap items-center'>
                                <input
                                    placeholder='Gas Consumption'
                                    className='hide-input-background text-sm input-color outline-none w-full p-1'
                                    type="number"
                                    step="0.1"
                                    disabled={!edit}
                                    name="gas_consumption"
                                    value={bill.gas_consumption !== "" ? bill.gas_consumption : ""}
                                    onChange={updateValue} />
                                <select
                                    name="gas_unit"
                                    className='text-sm input-color outline-none w-1/2 ml-2 hide-input-background'
                                    value={props.gasUnit ? props.gasUnit : ""}
                                    onChange={props.setGasUnit}>
                                    <option value="ft3">ft³</option>
                                    <option value="m3">m³</option>
                                    <option value="l">L</option>
                                    <option value="GJ">GJ</option>
                                </select>
                            </div>
                        </div>
                        <div className='mb-4'>
                            <p className='text-sm mb-1'>Gas Cost</p>
                            <div className='flex flex-nowrap items-center'>
                                <span className='mr-1 text-sm'>$</span>
                                <input
                                    placeholder='Gas Cost'
                                    className='hide-input-background text-sm input-color outline-none w-full p-1'
                                    type="number"
                                    step="0.1"
                                    disabled={!edit}
                                    name="gas_cost"
                                    defaultValue={bill.gas_cost !== "" ? bill.gas_cost : ""}
                                    onChange={updateValue} />
                            </div>
                        </div>
                    </div>
                }
                {
                    props.type === 2 &&
                    <div className='w-[130px]'>
                        <div className='mb-4'>
                            <p className='text-sm mb-1'>Water Consumption</p>
                            <div className='flex flex-nowrap items-center'>
                                <input
                                    placeholder='Water Consumption'
                                    className='hide-input-background text-sm input-color outline-none w-full p-1'
                                    type="number"
                                    step="0.1"
                                    disabled={!edit}
                                    name="water_consumption"
                                    value={bill.water_consumption !== "" ? bill.water_consumption : ""}
                                    onChange={updateValue} />
                                <select
                                    name="water_unit"
                                    className='text-sm input-color outline-none w-1/2 ml-2 hide-input-background'
                                    value={props.waterUnit ? props.waterUnit : ""}
                                    onChange={props.setWaterUnit}>
                                    <option value="ft3">ft³</option>
                                    <option value="m3">m³</option>
                                    <option value="l">L</option>
                                </select>
                            </div>
                        </div>
                        <div className='mb-4'>
                            <p className='text-sm mb-1'>Water Cost</p>
                            <div className='flex flex-nowrap items-center'>
                                <span className='mr-1 text-sm'>$</span>
                                <input
                                    placeholder='Water Cost'
                                    className='hide-input-background text-sm input-color outline-none w-full p-1'
                                    type="number"
                                    step="0.1"
                                    disabled={!edit}
                                    name="water_cost"
                                    defaultValue={bill.water_cost !== "" ? bill.water_cost : ""}
                                    onChange={updateValue} />
                            </div>
                        </div>
                    </div>
                }
                {
                    props.type === 2 &&
                    <div className='w-[130px]'>
                        <div className='mb-4'>
                            <p className='text-sm mb-1'>Sewage Consumption</p>
                            <div className='flex flex-nowrap items-center'>
                                <input
                                    placeholder='Sewage Consumption'
                                    className='hide-input-background text-sm input-color outline-none w-full p-1'
                                    type="number"
                                    step="0.1"
                                    disabled={!edit}
                                    name="sewage_consumption"
                                    value={bill.sewage_consumption !== "" ? bill.sewage_consumption : ""}
                                    onChange={updateValue} />
                                <select
                                    name="water_unit"
                                    className='text-sm input-color outline-none w-1/2 ml-2 hide-input-background'
                                    value={props.waterUnit ? props.waterUnit : ""}
                                    onChange={props.setWaterUnit}>
                                    <option value="ft3">ft³</option>
                                    <option value="m3">m³</option>
                                    <option value="l">L</option>
                                </select>
                            </div>
                        </div>
                        <div className='mb-4'>
                            <p className='text-sm mb-1'>Sewage Cost</p>
                            <div className='flex flex-nowrap items-center'>
                                <span className='mr-1 text-sm'>$</span>
                                <input
                                    placeholder='Sewage Cost'
                                    className='hide-input-background text-sm input-color outline-none w-full p-1'
                                    type="number"
                                    step="0.1"
                                    disabled={!edit}
                                    name="sewage_cost"
                                    defaultValue={bill.sewage_cost !== "" ? bill.sewage_cost : ""}
                                    onChange={updateValue} />
                            </div>
                        </div>
                    </div>
                }
                <div className='w-[110px]'>
                    <div className='mb-4'>
                        <p className='text-sm mb-1'>Delivery Charges</p>
                        <div className='flex flex-nowrap items-center'>
                            <span className='mr-1 text-sm'>$</span>
                            <input
                                placeholder='Delivery'
                                className='hide-input-background text-sm input-color outline-none w-full p-1'
                                type="number"
                                step="0.1"
                                disabled={!edit}
                                name="delivery_charges"
                                defaultValue={bill.delivery_charges !== "" ? bill.delivery_charges : ""}
                                onChange={updateValue} />
                        </div>
                    </div>
                    <div className='mb-4'>
                        <p className='text-sm mb-1'>Other Charges</p>
                        <div className='flex flex-nowrap items-center'>
                            <span className='mr-1 text-sm'>$</span>
                            <input
                                placeholder='Other'
                                className='hide-input-background text-sm input-color outline-none w-full p-1'
                                type="number"
                                step="0.1"
                                disabled={!edit}
                                name="other_charges"
                                defaultValue={bill.other_charges !== "" ? bill.other_charges : ""}
                                onChange={updateValue} />
                        </div>
                    </div>
                </div>
                {
                    [0, 1].includes(props.type) &&
                    <div className='w-[110px]'>
                        <div className='mb-4'>
                            <p className='text-sm mb-1'>Carbon Charge</p>
                            <div className='flex flex-nowrap items-center'>
                                <span className='mr-1 text-sm'>$</span>
                                <input
                                    placeholder='Carbon Charge'
                                    className='hide-input-background text-sm input-color outline-none w-full p-1'
                                    type="number"
                                    step="0.1"
                                    disabled={!edit}
                                    name="carbon_charge"
                                    defaultValue={bill.carbon_charge !== "" ? bill.carbon_charge : ""}
                                    onChange={updateValue} />
                            </div>
                        </div>
                        <div className='mb-4'>
                            <p className='text-sm mb-1'>Emissions</p>
                            <div className='flex flex-nowrap items-center'>
                                <input
                                    placeholder='Emissions'
                                    className='hide-input-background text-sm input-color outline-none w-full p-1'
                                    type="number"
                                    step="0.1"
                                    disabled={!edit}
                                    name="emissions"
                                    defaultValue={bill.emissions !== "" ? bill.emissions : ""}
                                    onChange={updateValue} />
                                <span className='ml-1 text-sm min-w-[50px]'>t CO₂e</span>
                            </div>
                        </div>
                    </div>}
                <div className='w-[80px]'>
                    <div className='mb-4'>
                        <p className='text-sm mb-1'>PST</p>
                        <div className='flex flex-nowrap items-center'>
                            <span className='mr-1 text-sm'>$</span>
                            <input
                                placeholder='PST'
                                className='hide-input-background text-sm input-color outline-none w-full p-1'
                                type="number"
                                step="0.1"
                                disabled={!edit}
                                name="pst_cost"
                                defaultValue={bill.pst_cost !== "" ? bill.pst_cost : ""}
                                onChange={updateValue} />
                        </div>
                    </div>
                    <div className='mb-4'>
                        <p className='text-sm mb-1'>GST</p>
                        <div className='flex flex-nowrap items-center'>
                            <span className='mr-1 text-sm'>$</span>
                            <input
                                placeholder='GST'
                                className='hide-input-background text-sm input-color outline-none w-full p-1'
                                type="number"
                                step="0.1"
                                disabled={!edit}
                                name="gst_cost"
                                defaultValue={bill.gst_cost !== "" ? bill.gst_cost : ""}
                                onChange={updateValue} />
                        </div>
                    </div>
                </div>
                <div className='w-[110px]'>
                    <div className='mb-4'>
                        <p className='text-sm mb-1'>Municipal Tax</p>
                        <div className='flex flex-nowrap items-center'>
                            <span className='mr-1 text-sm'>$</span>
                            <input
                                placeholder='Municipal Tax'
                                className='hide-input-background text-sm input-color outline-none w-full p-1'
                                type="number"
                                step="0.1"
                                disabled={!edit}
                                name="municipal_tax"
                                defaultValue={bill.municipal_tax !== "" ? bill.municipal_tax : ""}
                                onChange={updateValue} />
                        </div>
                    </div>
                    <div className='mb-4'>
                        <p className="text-sm mb-1 font-bold">Total</p>
                        <div className='flex flex-nowrap items-center'>
                            <span className='mr-1 text-sm'>$</span>
                            <input
                                placeholder='Total'
                                className='hide-input-background text-sm input-color outline-none w-full p-1 font-bold'
                                type="number"
                                step="0.1"
                                name="total"
                                disabled
                                value={props.value && totalList.reduce((t, k) => t + (parseFloat(props.value[k]) || 0), 0)} />
                        </div>
                    </div>
                </div>
                <div className='w-[110px]'>
                    <div className='mb-4'>
                        <p className='text-sm mb-1'>Uploaded By</p>
                        <div className='flex flex-nowrap items-center'>
                            <input
                                className='hide-input-background text-sm input-color outline-none w-full p-1'
                                type="text"
                                disabled
                                name="created_by"
                                defaultValue={bill.created_by ? bill.created_by : ""} />
                        </div>
                    </div>
                    <div className='mb-4'>
                        <p className='text-sm mb-1'>Updated By</p>
                        <div className='flex flex-nowrap items-center'>
                            <input
                                className='hide-input-background text-sm input-color outline-none w-full p-1'
                                type="text"
                                disabled
                                name="updated_by"
                                defaultValue={bill.updated_by ? bill.updated_by : ""} />
                        </div>
                    </div>
                </div>
                <div className=''>
                    <div className='flex flex-nowrap items-center justify-end'>
                        {
                            edit && !loading &&
                            <div className='mr-8 border-light-green block text-sm text-green4 font-medium cursor-pointer' onClick={() => setEdit(false)}>
                                Cancel
                            </div>
                        }
                        {
                            edit && acceptable && !loading &&
                            <button className='submit-button-small' onClick={postValue}>
                                Save
                            </button>
                        }
                        {
                            !edit && acceptable && !loading && bill.bill_url && bill.bill_url !== 'Manual Upload' &&

                            (downloading ?
                                <LoaderWheel /> :
                                <div className='mr-5'>
                                    <div onClick={() => handleDownload(bill.id)}>{GlobalSVG.download()}</div>
                                </div>)
                        }
                        {
                            !edit && acceptable && !loading && context.getUserInfo.manage_utility_bills &&
                            <button className='mr-5' onClick={() => setEdit(true)}>
                                {GlobalSVG.edit('#007b4e')}
                            </button>
                        }
                        {
                            !deleting && context.isGwUser && !edit && acceptable && !loading && context.getUserInfo.manage_utility_bills &&
                            <div className='cursor-pointer' onClick={handleDelete}>
                                {GlobalSVG.delete()}
                            </div>
                        }
                        {
                            (deleting || (edit && loading)) &&
                            <LoaderWheel />
                        }
                    </div>
                </div>
            </div>}
        </div>
    )
}

export default UtilityBillLine;
